/**
 * Utility functions for calculating effective stats with modifiers
 */

import useMetadataStore from '@store/useMetadataStore';

/**
 * Get the base value of a stat from the unit
 */
export function getBaseStat(unit, statName) {
  if (!unit) return 0;
  
  // Direct unit properties
  if (['hp', 'ap', 'movement'].includes(statName)) {
    if (unit[statName] === undefined || unit[statName] === null) {
      console.warn(`[getBaseStat] Missing stat '${statName}' on unit`, unit);
      return 0; // Return 0 but warn
    }
    return unit[statName];
  }
  
  // Template properties - note we use unit_template not unitTemplate
  const templateStats = [
    'max_hp', 'max_ap', 'max_movement',
    'strength', 'armor', 
    'agility', 'psi', 'will', 'vision'
  ];
  
  if (templateStats.includes(statName)) {
    // Map current values to max values if needed
    let templateField = statName;
    if (statName === 'hp') templateField = 'max_hp';
    if (statName === 'ap') templateField = 'max_ap';
    if (statName === 'movement') templateField = 'max_movement';
    
    if (!unit.unit_template || unit.unit_template[templateField] === undefined) {
      console.warn(`[getBaseStat] Missing template field '${templateField}' on unit`, unit);
      return 0; // Return 0 but warn
    }
    return unit.unit_template[templateField];
  }
  
  return 0;
}

/**
 * Get all active modifiers for a specific stat
 */
export function getActiveModifiers(unit, statName) {
  if (!unit?.active_effects || !Array.isArray(unit.active_effects)) {
    return [];
  }

  const modifiers = [];

  // Map display stat names to backend stat_modifier names
  // Same mapping as in getBaseStat()
  let modifierStatName = statName;
  if (statName === 'hp') modifierStatName = 'max_hp';
  if (statName === 'ap') modifierStatName = 'max_ap';
  if (statName === 'movement') modifierStatName = 'max_movement';

  unit.active_effects.forEach(unitEffect => {
    // Hydrate effect with metadata if needed
    useMetadataStore.getState().hydrateUnitEffect(unitEffect);
    // Check for stat modifiers using mapped name
    if (unitEffect.stat_modifiers?.[modifierStatName]) {
      // Validate source
      if (!unitEffect.source) {
        console.warn('[getActiveModifiers] Missing source for unitEffect:', unitEffect);
      }

      // Validate is_passive field
      if (unitEffect.effect.is_passive === undefined || unitEffect.effect.is_passive === null) {
        console.warn('[getActiveModifiers] Missing is_passive for unitEffect:', unitEffect);
      }

      // Only validate on_turn_start_counters if NOT passive
      if (!unitEffect.effect.is_passive) {
        if (!unitEffect.variables || unitEffect.variables.on_turn_start_counters === undefined || unitEffect.variables.on_turn_start_counters === null) {
          console.warn('[getActiveModifiers] Missing on_turn_start_counters for non-passive unitEffect:', unitEffect);
        }
      }

      // Extract the actual value from the modifier object
      // stat_modifiers contains objects like {value: -1, type: 'flat'}
      const modifierData = unitEffect.stat_modifiers[modifierStatName];
      const modifierValue = typeof modifierData === 'object' ? modifierData.value : modifierData;

      modifiers.push({
        value: modifierValue,
        source: unitEffect.source,
        effectId: unitEffect.effect_id,  // Add effect ID for icon display
        effectName: unitEffect.effect.name,  // Use nested effect.name - NO optional chaining
        on_turn_start_counters: unitEffect.variables ? unitEffect.variables.on_turn_start_counters : undefined,  // Will be undefined for passive effects (OK!)
        max_on_turn_start_counters: unitEffect.variables ? unitEffect.variables.max_on_turn_start_counters : undefined,  // Maximum duration for X/Y display
        unlimited: unitEffect.effect.is_passive,
        counter_state: unitEffect.counter_state || null  // Include counter state for counter effects
      });
    }
    // Special case for HP: also include DOT/HOT effects based on category and properties
    else if (statName === 'hp' && unitEffect.effect.category && unitEffect.properties) {
      // Access properties from on_turn_start trigger (C5 trigger-based structure)
      const onTurnStart = unitEffect.properties.on_turn_start || {};

      // Check if this is a DOT or HOT effect
      if (unitEffect.effect.category === 'dot' && onTurnStart.hp_damage !== undefined) {
        // DOT effect with HP damage
        const damage = onTurnStart.hp_damage;
        if (damage === undefined || damage === null) {
          console.warn('[getActiveModifiers] Missing on_turn_start.hp_damage for DOT unitEffect:', unitEffect);
        } else if (damage !== 0) {
          modifiers.push({
            value: -damage,  // Negative because it's damage
            source: unitEffect.source,
            effectId: unitEffect.effect_id,
            effectName: unitEffect.effect.name,
            duration: unitEffect.variables ? unitEffect.variables.on_turn_start_counters : undefined,
            max_turns: unitEffect.variables ? unitEffect.variables.max_on_turn_start_counters : undefined,  // Maximum duration for X/Y display
            unlimited: unitEffect.effect.is_passive,
            counter_state: unitEffect.counter_state || null  // Include counter state for counter effects
          });
        }
      } else if (unitEffect.effect.category === 'hot' && onTurnStart.heal_amount !== undefined) {
        // HOT effect with HP healing
        const healing = onTurnStart.heal_amount;
        if (healing === undefined || healing === null) {
          console.warn('[getActiveModifiers] Missing on_turn_start.heal_amount for HOT unitEffect:', unitEffect);
        } else if (healing !== 0) {
          modifiers.push({
            value: healing,  // Positive because it's healing
            source: unitEffect.source,
            effectId: unitEffect.effect_id,
            effectName: unitEffect.effect.name,
            duration: unitEffect.variables ? unitEffect.variables.on_turn_start_counters : undefined,
            max_turns: unitEffect.variables ? unitEffect.variables.max_on_turn_start_counters : undefined,  // Maximum duration for X/Y display
            unlimited: unitEffect.effect.is_passive,
            counter_state: unitEffect.counter_state || null  // Include counter state for counter effects
          });
        }
      }
    }
    // Similarly for armor
    else if (statName === 'armor' && unitEffect.effect.category === 'dot' && unitEffect.properties && unitEffect.properties.on_turn_start && unitEffect.properties.on_turn_start.armor_damage !== undefined) {
      const damage = unitEffect.properties.on_turn_start.armor_damage;
      if (damage === undefined || damage === null) {
        console.warn('[getActiveModifiers] Missing armor_damage_per_turn for DOT unitEffect:', unitEffect);
      } else if (damage !== 0) {
        modifiers.push({
          value: -damage,  // Negative because it's damage
          source: unitEffect.source,
          effectId: unitEffect.effect_id,
          effectName: unitEffect.effect.name,
          duration: unitEffect.variables ? unitEffect.variables.on_turn_start_counters : undefined,
          max_turns: unitEffect.variables ? unitEffect.variables.max_on_turn_start_counters : undefined,  // Maximum duration for X/Y display
          unlimited: unitEffect.effect.is_passive,
          counter_state: unitEffect.counter_state || null  // Include counter state for counter effects
        });
      }
    }
  });
  
  return modifiers;
}

/**
 * Calculate the total modifier value for a stat
 */
export function getTotalModifier(unit, statName) {
  const modifiers = getActiveModifiers(unit, statName);
  
  let total = 0;
  for (const modifier of modifiers) {
    if (modifier.value === undefined || modifier.value === null) {
      console.warn('[getTotalModifier] Missing value for modifier:', modifier);
    } else {
      total += modifier.value;
    }
  }
  
  return total;
}

/**
 * Get the effective value of a stat including all modifiers
 */
export function getEffectiveStat(unit, statName) {
  const baseStat = getBaseStat(unit, statName);
  const totalModifier = getTotalModifier(unit, statName);
  
  // Apply modifiers
  let effectiveValue = baseStat + totalModifier;
  
  // Stats that shouldn't go below 0
  if (['hp', 'max_hp', 'max_ap', 'max_movement', 'agility', 'vision', 'armor'].includes(statName)) {
    effectiveValue = Math.max(0, effectiveValue);
  }
  
  return effectiveValue;
}

/**
 * Format stat display with modifiers
 * Returns an object with display text and color class
 */
export function formatStatWithModifier(unit, statName) {
  const base = getBaseStat(unit, statName);
  const modifier = getTotalModifier(unit, statName);
  const effective = getEffectiveStat(unit, statName);
  
  if (modifier === 0) {
    return {
      text: String(base),
      className: '',
      hasModifier: false
    };
  }
  
  // Format: "base+modifier" or "base-modifier"
  const sign = modifier > 0 ? '+' : '';
  const text = `${base}${sign}${modifier}`;
  const className = modifier > 0 ? 'stat-buffed' : 'stat-debuffed';
  
  return {
    text,
    className,
    hasModifier: true,
    base,
    modifier,
    effective
  };
}

/**
 * Get detailed modifier breakdown for tooltips
 */
export function getModifierBreakdown(unit, statName) {
  const modifiers = getActiveModifiers(unit, statName);
  const base = getBaseStat(unit, statName);
  const total = getTotalModifier(unit, statName);
  const effective = getEffectiveStat(unit, statName);
  
  return {
    base,
    modifiers,
    total,
    effective
  };
}